import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, AlertCircle, Check } from 'lucide-react';

/**
 * SmartIngredientInput — searchable autocomplete that always resolves to an
 * ingredient_id from the canonical master.
 *
 * Props:
 *   ingredients     : array of {ingredient_id, ingredient_name|name, base_uom|ingredient_base_unit, category}
 *   value           : currently-selected ingredient_id (or '' if none)
 *   onChange        : (ingredient_id, ingredientObj) => void
 *   placeholder     : string
 *   testIdPrefix    : string used to derive data-testid hooks
 *   onEnter         : optional handler — called when user presses Enter on the input AND a selection exists.
 *                     Useful for "advance to next field" UX.
 *
 * Behavior:
 *   - Lists are pre-sorted alphabetically by display name (case-insensitive).
 *   - Matching is fuzzy: substring on display name, ingredient code (if present),
 *     and basic typo-tolerance via .localeCompare base sensitivity.
 *   - Suggestion row shows base_uom + category for disambiguation.
 *   - Arrow keys navigate, Enter selects.
 *   - Free-text without a selected ingredient_id is rejected (red border + error message).
 *     Parent should not allow save in that state — we expose `value === ''` for that.
 */
const SmartIngredientInput = ({
  ingredients = [],
  value = '',
  onChange,
  placeholder = 'Type to search ingredient…',
  testIdPrefix = 'ing-input',
  onEnter,
  autoFocus = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuRect, setMenuRect] = useState(null); // {top, left, width} in viewport coords
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // Sorted alphabetically once per ingredients change
  const sorted = useMemo(() => {
    const arr = [...ingredients];
    arr.sort((a, b) =>
      ((a.ingredient_name || a.name || '')).localeCompare(
        (b.ingredient_name || b.name || ''),
        undefined,
        { sensitivity: 'base' }
      )
    );
    return arr;
  }, [ingredients]);

  // Hydrate display query when value changes externally
  useEffect(() => {
    if (value) {
      const sel = sorted.find((i) => i.ingredient_id === value);
      if (sel) setQuery(sel.ingredient_name || sel.name || '');
    } else if (!showSuggest) {
      setQuery('');
    }
  }, [value, sorted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter
  const matches = useMemo(() => {
    if (!query || query.trim() === '') return sorted.slice(0, 20);
    const q = query.toLowerCase().trim();
    return sorted
      .map((i) => {
        const name = (i.ingredient_name || i.name || '').toLowerCase();
        let score = 0;
        if (name === q) score = 100;
        else if (name.startsWith(q)) score = 80;
        else if (name.includes(q)) score = 50;
        else if (q.length >= 3 && name.replace(/[^a-z]/g, '').includes(q.replace(/[^a-z]/g, ''))) score = 30;
        return { i, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map((r) => r.i);
  }, [query, sorted]);

  const isResolved = !!value && sorted.some((i) => i.ingredient_id === value);
  const showError = query.trim().length > 0 && !isResolved && !showSuggest && matches.length === 0;

  // ── floating menu position ───────────────────────────────────
  // Render the suggestion list through a portal to document.body using fixed
  // positioning so it always escapes parent `overflow:hidden / auto` / table
  // / dialog stacking contexts and floats above every row & card.
  const recalcPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuRect({
      top: r.bottom + 4,    // 4px gap below input
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (!showSuggest) return;
    recalcPosition();
    const onScroll = () => recalcPosition();
    const onResize = () => recalcPosition();
    // capture=true catches scroll on ANY ancestor (table, dialog body, etc.)
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [showSuggest, matches.length, query]);

  // Outside-click closer (mousedown so it fires before suggestion onMouseDown)
  useEffect(() => {
    if (!showSuggest) return;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setShowSuggest(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSuggest]);

  const select = (ing) => {
    onChange?.(ing.ingredient_id, ing);
    setQuery(ing.ingredient_name || ing.name || '');
    setShowSuggest(false);
    setHighlight(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowSuggest(true);
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (showSuggest && matches[highlight]) {
        e.preventDefault();
        select(matches[highlight]);
      } else if (isResolved && onEnter) {
        e.preventDefault();
        onEnter();
      }
    } else if (e.key === 'Escape') {
      if (showSuggest) {
        // Only close the dropdown — don't let Radix Dialog also close.
        e.stopPropagation();
        setShowSuggest(false);
      }
    }
  };

  const onChangeRaw = (e) => {
    setQuery(e.target.value);
    setShowSuggest(true);
    setHighlight(0);
    // If user is editing the text but had a selection, clear the bound id
    // so parent re-validates. Reselection via Enter or click re-binds it.
    if (value) onChange?.('', null);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onChangeRaw}
          onFocus={() => { setShowSuggest(true); recalcPosition(); }}
          onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-text`}
          aria-autocomplete="list"
          className={`w-full pl-8 pr-8 py-2 bg-[#18181b] border rounded-md text-sm outline-none transition-colors ${
            isResolved ? 'border-emerald-500/40' : (showError ? 'border-red-500/60' : 'border-[#3f3f46] focus:border-emerald-500')
          }`}
        />
        {isResolved && (
          <Check
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none"
            data-testid={`${testIdPrefix}-resolved`}
          />
        )}
      </div>
      {showError && (
        <p className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="w-3 h-3" /> Ingredient not found. Please select from master list.
        </p>
      )}
      {showSuggest && matches.length > 0 && menuRect && createPortal(
        <ul
          ref={listRef}
          data-testid={`${testIdPrefix}-suggestions`}
          style={{
            position: 'fixed',
            top: menuRect.top,
            left: menuRect.left,
            width: menuRect.width,
            zIndex: 10000,
          }}
          className="bg-[#0c0c0e] border border-[#3f3f46] rounded-md max-h-64 overflow-y-auto shadow-xl"
        >
          {matches.map((ing, idx) => {
            const name = ing.ingredient_name || ing.name || '';
            const unit = ing.base_uom || ing.ingredient_base_unit || '';
            const cat = ing.category || ing.ingredient_category || '';
            return (
              <li
                key={ing.ingredient_id}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => { e.preventDefault(); select(ing); }}
                data-testid={`${testIdPrefix}-suggestion-${idx}`}
                className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm ${
                  idx === highlight ? 'bg-emerald-500/15 text-emerald-100' : 'hover:bg-[#18181b]'
                }`}
              >
                <span className="truncate flex-1">{name}</span>
                <span className="ml-2 shrink-0 flex items-center gap-2 text-xs text-zinc-500">
                  {unit && <span className="font-mono">{unit}</span>}
                  {cat && <span className="px-1.5 py-0.5 rounded bg-[#27272a]">{cat}</span>}
                </span>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default SmartIngredientInput;
